import styled, { css } from 'styled-components';
import { Grid } from '@interstate/components/Grid';
import { BreakPoint } from '@makemydeal/dr-activities-common';

const CALCULATED_COLUMN_TINT = 'rgba(255, 189, 147, 0.2)';

export const StyledPaymentGridContainer = styled(Grid)<{
    $isDealCentralExperience: boolean;
}>`
    padding: ${({ $isDealCentralExperience }) => ($isDealCentralExperience ? '0px' : '0 20px')};
    margin-top: 0;
    position: relative;
    height: auto;
    align-items: center;
    justify-content: center;
    & > div:first-of-type {
        border-top-left-radius: 0.2rem;
    }
    & > div:nth-of-type(4) {
        border-top-right-radius: 0.2rem;
    }
`;

const growToFitCss = (minHeight: string) => css`
    height: auto;
    min-height: ${minHeight};
    align-self: stretch;
    justify-content: flex-start;
    padding-bottom: 6px;

    &&.MuiGrid2-root {
        padding-bottom: 6px;
    }
`;

export const GridItem = styled(Grid)<{ $isCalculated?: boolean; $growToFit?: boolean }>`
    border: 1px solid #b2b2b2;
    width: 120px;
    height: 80px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-sizing: border-box;
    background-color: ${(props) => (props.$isCalculated ? CALCULATED_COLUMN_TINT : undefined)};
    ${(props) => props.$growToFit && growToFitCss('80px')}

    @media (max-width: ${BreakPoint.SMALL}) {
        &.MuiGrid2-root {
            padding: 0 1%;
        }
    }
    @media (max-width: ${BreakPoint.MEDIUM}) {
        &.MuiGrid2-root {
            padding: 0 3%;
        }
    }
`;

/**
 * Shorter cell for the Residual row. It sits immediately under the Sell Rate row, so it does not need
 * GridItem's full 80px — that height spaces the rate row away from the payment cells above it. With the unit
 * toggle rendered the cell carries a second control and takes the taller height instead.
 */
export const CompactGridItem = styled(GridItem)<{ $withUnitToggle?: boolean }>`
    height: ${(props) => (props.$withUnitToggle ? '92px' : '56px')};
    ${(props) => props.$growToFit && growToFitCss(props.$withUnitToggle ? '92px' : '56px')}
`;

export const StyledResidualUnitToggle = styled.div<{ $disabled?: boolean }>`
    margin-top: 4px;
    width: 100%;
    ${(props) => (props.$disabled ? 'opacity: 0.5;' : '')}

    .MuiToggleButtonGroup-root {
        width: 100%;
    }

    button.MuiToggleButtonGroup-grouped {
        flex: 1;
        min-width: 0;
    }

    button.MuiToggleButton-root:focus-visible {
        outline: 2px solid #005ba8;
        outline-offset: -2px;
    }
`;

export const TermGridItem = styled(GridItem)<{ $hasCalculatedColumn?: boolean }>`
    ${(props) =>
        props.$hasCalculatedColumn &&
        css`
            height: auto;
            min-height: 80px;
            align-self: stretch;
            justify-content: flex-start;
            padding-top: 8px;
        `}
`;

export const StyledGridItem = styled(GridItem)<{ $isSelected?: boolean; $canSelect?: boolean; $isCalculated?: boolean }>`
    background-color: ${(props) => (props.$isSelected ? '#003468' : props.$isCalculated ? CALCULATED_COLUMN_TINT : '#ffffff')};
    color: ${(props) => (props.$isSelected ? 'white' : '#16171A')};
    cursor: ${(props) => (props.$canSelect ? 'pointer' : 'default')};
`;

export const StyledTypography = styled.div`
    font-size: 12px;
    &.large {
        font-size: 16px;
    }
`;

export const CalculatedColumnBadgeWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: ${({ theme }) => theme.tokens?.BaseSpace10 || '4px'};
`;

export const CalculatedCellRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.tokens?.BaseSpace15 || '6px'};
`;

export const CalculatedCellIconButton = styled.button`
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
`;

export const StyledMonthsTypography = styled.div<{ $isSelected?: boolean }>`
    font-size: 11px;
    color: ${(props) => (props.$isSelected ? 'white' : '#6d6d6d')};
`;

export const StyledDivider = styled.div<{ $isSelected?: boolean }>`
    width: 50%;
    height: 1px;
    background-color: ${(props) => (props.$isSelected ? 'white' : '#6d6d6d')};
    opacity: 0.8;
    margin: 8px 0;
`;

export const LoadingIndicatorWrapper = styled.div`
    position: relative;

    & #interstitial-overlay-root {
        background-color: transparent;
    }
    & #interstitial-content-root {
        box-shadow: none;
    }
`;

export const StyledGridContainer = styled.div`
    container-name: ContainerGrid;
    container-type: inline-size;

    @container (min-width: 0px) {
        .payment-grid-item {
            padding: 0 1%;
        }
        // this is needed to make Interstate input digits smaller on small screen
        .sell-rate-input input,
        .residual-input input {
            font-size: 15px;
        }
        // this is needed to remove gaps on Interstate input on small screen
        .MuiInputBase-root {
            gap: 3px !important;
            padding: 0 0.25rem 0 0.5rem !important;
        }
    }

    @container (min-width: ${BreakPoint.PHONE}) {
        .payment-grid-item {
            padding: 0 3%;
        }
        .sell-rate-input input,
        .residual-input input {
            font-size: 16px !important;
        }
        .MuiInputBase-root {
            gap: 0.5rem !important;
            padding: 0 0.5rem 0 0.75 !important;
        }
    }

    @container (min-width: ${BreakPoint.SMALL}) {
        .payment-grid-item {
            padding: 0 5%;
        }
        .sell-rate-input input,
        .residual-input input {
            font-size: 16px !important;
        }
        .MuiInputBase-root {
            gap: 0.5rem !important;
            padding: 0 0.5rem 0 0.75 !important;
        }
    }
`;

export const StyledGridRowContainer = styled(Grid)`
    display: contents;

    & > div:first-of-type {
        background: #f4f4f4;
    }
`;
